import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { BiaMasterService } from 'src/app/pages/sa/bia-master/bia-master.service';
import * as fromSaBia from './sa-bia.selectors';
import { forkJoin, of } from 'rxjs';
import * as SaBiaActions from './sa-bia.actions';
import { Store } from '@ngrx/store';
import * as Helper from '../helpers/helper.effects';
import { createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { updateStatusReset } from '../status/status.actions';
import { StatusType } from 'src/app/core/enum/status.enum';
import { CommonMessageService } from 'src/app/services/common-message.service';

@Injectable()
export class SaBiaEffects {
  loadImpactTemplates$ = Helper.createEffectModified(this, {
    lookupType: SaBiaActions.loadImpactTemplates,
    entitySelector: fromSaBia.getImpactTemplates,
    service: () => this.saBiaService.getImpactTemplates(),
    callback: (response: any) => {
      return SaBiaActions.loadImpactTemplateSuccess({
        impactTemplates: response.body,
      });
    },
    dummyAction: SaBiaActions.dummyAction,
  });

  loadImpactTemplateGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.loadITGroups),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(this.store.select(fromSaBia.getITGroups))
          ),
        (action, data) => data
      ),
      switchMap(([action, itg]) => {
        if (!itg || itg.length == 0) {
          return this.saBiaService.getImpactTemplateGroupData(action.id).pipe(
            concatMap((response) => {
              if (response.body == null) {
                return this.saBiaService.getImpactTemplate(action.id).pipe(
                  map((resp) => ({
                    saImpactTemplate: resp.body,
                    saMaos: [],
                    saRtos: [],
                    saImpactTypes: [],
                    saImpactValues: [],
                  }))
                );
              }

              const itdata = response.body;
              const saImpactTemplate = itdata;
              const { saBiaImpactTemplateGroups } = itdata;
              const saMaos = saBiaImpactTemplateGroups
                .filter((itd) => itd.saMao)
                .map((itd) => ({
                  ...itd.saMao,
                  rto: itd.saRto,
                  itgId: itd.id,
                }));
              const saRtos = saBiaImpactTemplateGroups
                .filter((itd) => itd.saRto)
                .map((itd) => ({ ...itd.saRto, itgId: itd.id }));
              const saImpactTypes = saBiaImpactTemplateGroups
                .filter((itd) => itd.saImpactType)
                .map((itd) => ({ ...itd.saImpactType, itgId: itd.id }));
              const saImpactValues = saImpactTypes
                .map((it) => it.saImpactValues)
                .reduce((all, single) => {
                  return all.concat(single);
                }, []);
              return of({
                saImpactTemplate,
                saMaos,
                saRtos,
                saImpactTypes,
                saImpactValues,
              });
            }),
            map((result) =>
              SaBiaActions.loadITGroupSuccess({
                iTGroups: result,
              })
            )
          );
        }
        return of(SaBiaActions.dummyAction());
      })
    );
  });

  loadImpactTypes$ = Helper.createEffectModified(this, {
    lookupType: SaBiaActions.loadImpactTypes,
    entitySelector: fromSaBia.getImpactTypes,
    service: () => this.saBiaService.getImpactTypes(),
    callback: (response: any) => {
      return SaBiaActions.loadImpactTypeSuccess({
        impactTypes: response.body,
      });
    },
    dummyAction: SaBiaActions.dummyAction,
  });

  loadImpactValues$ = Helper.createEffectModified(this, {
    lookupType: SaBiaActions.loadImpactValues,
    entitySelector: fromSaBia.getImpactValues,
    service: () => this.saBiaService.getImpactValues(),
    callback: (response: any) => {
      return SaBiaActions.loadImpactValueSuccess({
        impactValues: response.body,
      });
    },
    dummyAction: SaBiaActions.dummyAction,
  });

  addMaos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.addMaos),
      switchMap((action) => {
        if (!action.maos) return of(SaBiaActions.dummyAction());

        const services = { mao: this.saBiaService.addMaos(action.maos) };
        if (action.rto) services['rto'] = this.saBiaService.addRto(action.rto);

        return forkJoin(services).pipe(
          switchMap(({ mao, rto }: { mao: any; rto: any }) => {
            return this.saBiaService
              .addImpactTemplateGroup({
                saImpactTemplate: { id: action.templateId },
                saMao: { id: mao['id'] },
                saRto: rto ? { id: rto['id'] } : undefined,
              })
              .pipe(
                map((impactGroup) => {
                  return SaBiaActions.addMaosSuccess({
                    maos: { ...mao, rto, itgId: impactGroup.id },
                    rto: rto ? { ...rto, itgid: impactGroup.id } : null,
                  });
                })
              );
          })
        );
      })
    );
  });

  updateMaos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateMaos),
      switchMap((action) => {
        const { maos, rto } = action;
        if (!maos) return of(SaBiaActions.dummyAction());

        const services = { mao: this.saBiaService.updateMaos(maos.id, maos) };
        if (action.rto) {
          services['rto'] = this.saBiaService.updateRto(rto.id, rto);
        }
        return forkJoin(services).pipe(
          map(({ mao, rto }) => {
            if (!mao) return SaBiaActions.dummyAction();
            return SaBiaActions.updateMaosSuccess({
              maos: mao,
              rto,
            });
          })
        );
      })
    );
  });

  deleteMao$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.deleteMao),
      switchMap((action) => {
        const { mao } = action;
        if (!mao) return of(SaBiaActions.dummyAction());

        return this.saBiaService.deleteMao(mao.id).pipe(
          map(() => {
            return SaBiaActions.deleteMaoSuccess({ mao });
          })
        );
      })
    );
  });

  addRto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.addRto),
      switchMap((action) => {
        if (!action.rtos) return of(SaBiaActions.dummyAction());

        return this.saBiaService.addRto(action.rtos).pipe(
          switchMap((rto) => {
            return this.saBiaService
              .addImpactTemplateGroup({
                saImpactTemplate: { id: action.templateId },
                saRto: rto ? { id: rto['id'] } : undefined,
              })
              .pipe(
                map((impactGroup) => {
                  return SaBiaActions.addRtoSuccess({
                    rtos: { ...rto, itgId: impactGroup.id },
                  });
                })
              );
          })
        );
      })
    );
  });

  updateRto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateRto),
      mergeMap((action) => {
        const { rtos } = action;
        if (!rtos) return of(SaBiaActions.dummyAction());

        return this.saBiaService.updateRto(rtos.id, rtos).pipe(
          map((rto) => {
            if (!rto) return SaBiaActions.dummyAction();

            return SaBiaActions.updateRtoSuccess({
              rtos: rto,
            });
          })
        );
      })
    );
  });

  deleteRto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.deleteRto),
      switchMap((action) => {
        const { rto } = action;
        if (!rto) return of(SaBiaActions.dummyAction());

        return this.saBiaService.deleteRto(rto.id).pipe(
          map(() => {
            return SaBiaActions.deleteRtoSuccess({ rto });
          })
        );
      })
    );
  });

  addImpactType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.addImpactType),
      switchMap((action) => {
        if (!action.impactTypes) return of(SaBiaActions.dummyAction());

        return this.saBiaService.addImpactTypes(action.impactTypes).pipe(
          switchMap((it) => {
            return this.saBiaService
              .addImpactTemplateGroup({
                saImpactTemplate: { id: action.templateId },
                saImpactType: it ? { id: it['id'] } : undefined,
              })
              .pipe(
                map((impactGroup) => {
                  return SaBiaActions.addImpactTypeSuccess({
                    impactTypes: { ...it, itgId: impactGroup.id },
                  });
                })
              );
          })
        );
      })
    );
  });

  updateImpactType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateImpactType),
      switchMap((action) => {
        const { impactTypes: its } = action;
        if (!its) return of(SaBiaActions.dummyAction());
        return this.saBiaService.updateImpactTypes(its.id, its).pipe(
          map((it) => {
            if (!it) return SaBiaActions.dummyAction();

            return SaBiaActions.updateImpactTypeSuccess({
              impactTypes: it,
            });
          })
        );
      })
    );
  });

  deleteImpactType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.deleteImpactType),
      switchMap((action) => {
        const { impactType } = action;
        if (!impactType) return of(SaBiaActions.dummyAction());

        return this.saBiaService.deleteImpactType(impactType.id).pipe(
          map(() => {
            return SaBiaActions.deleteImpactTypeSuccess({ impactType });
          })
        );
      })
    );
  });

  addImpactValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.addImpactValue),
      switchMap((action) => {
        if (!action.impactValue) return of(SaBiaActions.dummyAction());

        return this.saBiaService.addImpactValue(action.impactValue).pipe(
          map((impv) => {
            if (!impv) return SaBiaActions.dummyAction();

            return SaBiaActions.addImpactValueSuccess({
              impactValue: impv,
            });
          })
        );
      })
    );
  });

  updateImpactValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateImpactValue),
      switchMap((action) => {
        const { impactValue } = action;
        if (!impactValue) return of(SaBiaActions.dummyAction());

        return this.saBiaService
          .updateImpactValues(impactValue.id, impactValue)
          .pipe(
            map((impv) => {
              if (!impv) return SaBiaActions.dummyAction();

              return SaBiaActions.updateImpactValueSuccess({
                impactValue: impv,
              });
            })
          );
      })
    );
  });

  deleteImpactValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.deleteImpactValue),
      switchMap((action) => {
        const { impactValue } = action;
        if (!impactValue) return of(SaBiaActions.dummyAction());

        return this.saBiaService.deleteImpactValue(impactValue.id).pipe(
          map(() => {
            return SaBiaActions.deleteImpactValueSuccess({ impactValue });
          })
        );
      })
    );
  });

  updateImpactTemplate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateImpactTemplate),
      switchMap((action) => {
        const { impactTemplates: it } = action;
        if (!it) return of(SaBiaActions.dummyAction());

        return this.saBiaService.updateImpactTemplate(it.id, it).pipe(
          map((impT) => {
            if (!impT) return SaBiaActions.dummyAction();
            return SaBiaActions.updateImpactTemplateSuccess({
              impactTemplates: impT,
            });
          })
        );
      })
    );
  });

  loadRaInternalQues$ = Helper.createEffectModified(this, {
    lookupType: SaBiaActions.loadSaRaIntQues,
    entitySelector: fromSaBia.getRaInQues,
    service: () => this.saBiaService.getRaInternalQues(),
    callback: (response: any) => {
      return SaBiaActions.loadSaRaIntQuesSuccess({
        raIntQues: response.body,
      });
    },
    dummyAction: SaBiaActions.dummyAction,
  });

  loadCriticalDef$ = Helper.createEffectModified(this, {
    lookupType: SaBiaActions.loadCriticalDef,
    entitySelector: fromSaBia.getCriticalDef,
    service: () => this.saBiaService.getCriticalDef(),
    callback: (response: any) => {
      return SaBiaActions.loadCriticalDefSuccess({
        criticalDef: response.body,
      });
    },
    dummyAction: SaBiaActions.dummyAction,
  });

  addRaInternalQues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.addSaRaIntQues),
      switchMap((action) => {
        if (!action.raInternalQues) return of(SaBiaActions.dummyAction());
        return this.saBiaService.addRaInteranlQues(action.raInternalQues).pipe(
          map((sr) => {
            if (!sr) return SaBiaActions.dummyAction();
            updateStatusReset(StatusType.Created, this.store);
            return SaBiaActions.addSaRaIntQuesSuccess({
              raInternalQues: sr,
            });
          }),
          // catchError(() =>
          //   of(updateStatusReset(StatusType.Failure, this.store))
          // )
          catchError((error: any) => {
            if (error.includes('409')) {
              this.commonMessageService.warn('Duplicate Question');
              return;
            }
            return of(updateStatusReset(StatusType.Failure, this.store));
          })
        );
      })
    );
  });

  updateRaInternalQues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.updateSaRaIntQues),
      switchMap((action) => {
        const { raInternalQues } = action;
        if (!raInternalQues) return of(SaBiaActions.dummyAction());
        return this.saBiaService
          .updateRaInternalQues(raInternalQues.id, raInternalQues)
          .pipe(
            map((raq) => {
              if (!raq) return SaBiaActions.dummyAction();
              updateStatusReset(StatusType.Updated, this.store);
              return SaBiaActions.updateSaRaIntQuesSuccess({
                raInternalQues: raq,
              });
            }),
            // catchError(() =>
            //   of(updateStatusReset(StatusType.Failure, this.store))
            // )
            catchError((error: any) => {
              if (error.includes('409')) {
                this.commonMessageService.warn('Duplicate Question');
                return;
              }
              return of(updateStatusReset(StatusType.Failure, this.store));
            })
          );
      })
    );
  });

  deleteSaRaIntQues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SaBiaActions.deleteSaRaIntQues),
      switchMap((action) => {
        const { raInternalQues } = action;
        if (!raInternalQues) return of(SaBiaActions.dummyAction());
        raInternalQues.isEnabled = false;
        return this.saBiaService
          .deleteRaInternalQues(raInternalQues.id, raInternalQues)
          .pipe(
            map(() => {
              updateStatusReset(StatusType.Deleted, this.store);
              return SaBiaActions.deleteSaRaIntQuesSuccess({ raInternalQues });
            }),
            catchError(() =>
              of(updateStatusReset(StatusType.Failure, this.store))
            )
          );
      })
    );
  });

  constructor(
    public actions$: Actions,
    public store: Store,
    private saBiaService: BiaMasterService,
    private commonMessageService: CommonMessageService
  ) {}
}
